import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  FormControl,
  Button,
  TextInput,
  ButtonGroup,
} from "@primer/react";
import { Dialog } from "@primer/react/drafts";
import { SyncIcon, PlusIcon } from "@primer/octicons-react";
import { useCommonContext } from "../../../../contexts/common";
import { thiSinhApi } from "../../../../api/thiSinhApi";
import { NotifyHelper } from "../../../../helpers/toast";
import DataTable from "../../../../components/ui/data-table";
import moment from "moment";
import ComboboxKhoi from "../../../../components/combobox-khoi";
import ComboboxHe from "../../../../components/combobox-he";
import Modal from "../../../../components/ui/modal";

interface ThiSinhData {
  id: number;
  trang_thai: string;
  ngaythi_dauvao_dukien: string;
  ma_hs: string;
  ho_ten: string;
  ngay_sinh: string;
  gioi_tinh: string;
  ten_khoi: string;
  ten_he: string;
}

interface AddThiSinhModalProps {
  dm_truong_id: number;
  ex_kythi_id: number;
  nam_hoc: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddThiSinhModal: React.FC<AddThiSinhModalProps> = ({
  dm_truong_id,
  ex_kythi_id,
  nam_hoc,
  onCancel,
  onSuccess,
}) => {
  const { translate } = useCommonContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [ngayThiDuKien, setNgayThiDuKien] = useState("");
  const [dsHocSinhs, setDSHocSinhs] = useState<ThiSinhData[]>([]);

  const [filters, setFilters] = useState({
    nam_hoc,
    dm_truong_id,
    ex_kythi_id,
    ngaythi_dauvao_dukien: "",
    dm_khoi_id: 0,
    dm_he_id: 0,
    da_thi: false,
  });

  const loadHocSinhs = useCallback(async () => {
    setIsLoading(true);
    setSelectedIds([]);
    try {
      const res = await thiSinhApi.selectHocSinh(filters);
      if (res.is_success) {
        setDSHocSinhs(res.data);
      } else {
        setDSHocSinhs([]);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
      setDSHocSinhs([]);
    }
    setIsLoading(false);
  }, [filters]);

  const handleAddThiSinh = async () => {
    if (ex_kythi_id <= 0 || selectedIds.length === 0) return;

    setIsSaving(true);
    try {
      const res = await thiSinhApi.addHocSinh({
        ex_kythi_id,
        ts_hocsinh_ids: selectedIds,
      });

      if (res.is_success) {
        NotifyHelper.Success(translate("Success.Add"));
        onSuccess();
        loadHocSinhs();
      } else {
        NotifyHelper.Error(res.message || translate("Error.Generic"));
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.Generic"));
    }
    setIsSaving(false);
  };

  useEffect(() => {
    loadHocSinhs();
  }, [filters]);

  const filteredData = useMemo(() => {
    if (!dsHocSinhs.length) return [];
    if (!ngayThiDuKien) return dsHocSinhs;

    return dsHocSinhs.filter(
      (x) =>
        moment(x.ngaythi_dauvao_dukien).format("YYYY-MM-DD") === ngayThiDuKien
    );
  }, [dsHocSinhs, ngayThiDuKien]);

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Modal
      isOpen
      title={translate("Thêm thí sinh")}
      onClose={onCancel}
      width={"100%"}
    >
      <Box sx={{ p: 0 }}>
        {/* <Dialog.Header>
          {translate('ThiSinh.ListTitle')}
        </Dialog.Header> */}

        <Dialog.Body>
          {/* <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "flex-end" }}></Box>
          </Box> */}

          {/* <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Text>
              {translate("Tổng số")}:{" "}
              <Text fontWeight="bold">{filteredData.length}</Text>
            </Text>
            {selectedIds.length > 0 && (
              <Text sx={{ color: "success.fg" }}>
                {translate("Selected")}:{" "}
                <Text fontWeight="bold">{selectedIds.length}</Text>
              </Text>
            )}
          </Box> */}

          <DataTable
            data={filteredData}
            isLoading={isLoading}
            selection={{
              mode: "multiple",
              selectedRowKeys: selectedIds,
              onSelectionChanged: setSelectedIds,
            }}
            actionComponent={
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={{ width: "200px" }}>
                  <ComboboxKhoi
                    value={filters.dm_khoi_id}
                    onValueChanged={(value) =>
                      handleFilterChange("dm_khoi_id", value)
                    }
                    isShowClearButton
                    dm_truong_id={dm_truong_id}
                  />
                </FormControl>
                <FormControl sx={{ width: "200px" }}>
                  <ComboboxHe
                    value={filters.dm_he_id}
                    onValueChanged={(value) =>
                      handleFilterChange("dm_he_id", value)
                    }
                    isShowClearButton
                    dm_truong_id={dm_truong_id}
                    dm_khoi_id={filters.dm_khoi_id}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <TextInput
                    type="date"
                    value={ngayThiDuKien}
                    onChange={(e) => setNgayThiDuKien(e.target.value)}
                  />
                </FormControl>
                <Button
                  variant="default"
                  onClick={loadHocSinhs}
                  leadingVisual={SyncIcon}
                  disabled={isLoading}
                >
                  {translate("Base.Label.Refresh")}
                </Button>
              </Box>
            }
            columns={[
              {
                dataField: "trang_thai",
                caption: translate("Trạng thái"),
                width: "100px",
              },
              {
                dataField: "ngaythi_dauvao_dukien",
                caption: translate("Ngày thi dự kiến"),
                width: "140px",
              },
              {
                dataField: "ma_hs",
                caption: translate("Mã học sinh"),
                width: "120px",
              },
              {
                dataField: "ho_ten",
                caption: translate("Họ tên"),
                minWidth: "150px",
              },
              {
                dataField: "ngay_sinh",
                caption: "Ngày sinh",
                cellRender: (data) => (
                  <Text>
                    {data.ngay_sinh
                      ? moment(data.ngay_sinh).format("DD/MM/YYYY")
                      : ""}
                  </Text>
                ),
              },
              {
                dataField: "gioi_tinh",
                caption: translate("Giới tính"),
                width: "100px",
              },
              {
                dataField: "ten_khoi",
                caption: translate("Khối"),
                width: "100px",
              },
              {
                dataField: "ten_he",
                caption: translate("Hệ"),
                width: "100px",
              },
            ]}
            paging={{ enable: true, pageSize: 50 }}
            searchEnable={true}
          />
        </Dialog.Body>

        <Dialog.Footer>
          <ButtonGroup>
            <Button variant="default" onClick={onCancel} disabled={isSaving}>
              {translate("Base.Label.Cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleAddThiSinh}
              disabled={selectedIds.length === 0 || isSaving}
              leadingVisual={PlusIcon}
            >
              {translate("Thêm thí sinh")} ({selectedIds.length})
            </Button>
          </ButtonGroup>
        </Dialog.Footer>
      </Box>
    </Modal>
  );
};

export default AddThiSinhModal;
